import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/hooks/useTenant";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Bell, HeartPulse, MessageSquare, Dumbbell, X, Check, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  student_id: string | null;
  reference_id: string | null;
  read_at: string | null;
  created_at: string;
}

const typeIcons: Record<string, typeof Bell> = {
  pain_report: HeartPulse,
  new_message: MessageSquare,
  workout_pending_approval: Dumbbell,
};

const typeColors: Record<string, string> = {
  pain_report: "text-destructive bg-destructive/10",
  new_message: "text-primary bg-primary/10",
  workout_pending_approval: "text-amber-400 bg-amber-500/10",
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { permission, subscribed, subscribe, loading: pushLoading } = usePushNotifications();

  const { data: notifications } = useQuery({
    queryKey: ["professor-notifications"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("professor_notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data || []) as Notification[];
    },
    enabled: !!tenantId,
    refetchInterval: 30000,
  });

  // Realtime subscription
  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase
      .channel("professor-notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "professor_notifications",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [tenantId, queryClient]);

  const unreadCount = notifications?.filter((n) => !n.read_at).length || 0;

  const markAsRead = async (id: string) => {
    await (supabase as any)
      .from("professor_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
  };

  const markAllRead = async () => {
    const unreadIds = notifications?.filter((n) => !n.read_at).map((n) => n.id) || [];
    if (unreadIds.length === 0) return;
    await (supabase as any)
      .from("professor_notifications")
      .update({ read_at: new Date().toISOString() })
      .in("id", unreadIds);
    queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
  };

  const handleClick = (n: Notification) => {
    markAsRead(n.id);
    if (n.student_id) {
      navigate(`/student/${n.student_id}`);
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="ghost"
        className="h-8 px-2 text-muted-foreground hover:text-foreground relative"
        onClick={() => setOpen(!open)}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </motion.span>
        )}
      </Button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-10 z-50 w-[340px] sm:w-[380px] rounded-xl border border-border bg-card shadow-xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Notificações</h3>
                <div className="flex gap-1">
                  {unreadCount > 0 && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={markAllRead}>
                      <Check className="h-3 w-3 mr-1" />
                      Marcar tudo
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setOpen(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Push enable banner */}
              {!subscribed && permission !== "denied" && (
                <div className="px-4 py-2.5 border-b border-border bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BellRing className="h-3.5 w-3.5 text-primary" />
                      <span className="text-[11px] text-muted-foreground">Receba alertas mesmo offline</span>
                    </div>
                    <Button size="sm" variant="default" className="h-6 text-[10px] px-2" onClick={subscribe} disabled={pushLoading}>
                      Ativar Push
                    </Button>
                  </div>
                </div>
              )}

              {/* List */}
              <div className="max-h-[400px] overflow-y-auto">
                {!notifications?.length ? (
                  <div className="py-10 text-center">
                    <Bell className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma notificação</p>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const Icon = typeIcons[n.type] || Bell;
                    const colorClasses = typeColors[n.type] || "text-muted-foreground bg-muted";

                    return (
                      <button
                        key={n.id}
                        onClick={() => handleClick(n)}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-accent/30 transition-colors border-b border-border/50 last:border-0 ${
                          !n.read_at ? "bg-primary/[0.03]" : ""
                        }`}
                      >
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${colorClasses}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-xs font-medium truncate ${!n.read_at ? "text-foreground" : "text-muted-foreground"}`}>
                              {n.title}
                            </p>
                            {!n.read_at && (
                              <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1" />
                            )}
                          </div>
                          {n.body && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground/60 mt-1">
                            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: ptBR })}
                          </p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
